import addDate from './addDate';
import parseIsoDate from './parseIsoDate';

/**
 * Parses the first-available-date prop, used to block the calendar days between
 * today and a given date.
 *
 * @param {String} firstAvailableDate - First selectable date in `YYYY-MM-DD` format
 * @param {Number} [maxDaysSearch] - Days of anticipation the search allows. When given, a first
 * available date beyond that window is unusable and gets ignored
 * @returns {Date|null} - The date to use as the calendar minimum, or null when there is
 * nothing to block, so the calendar falls back to today
 */
export default function getFirstAvailableDate(firstAvailableDate, maxDaysSearch) {
  const dateToValidate = parseIsoDate(firstAvailableDate);
  if (!dateToValidate) return null;

  // Getting the date without time to take the today date with the 00:00 hour
  const today = new Date(new Date().setHours(0, 0, 0, 0));

  // Once the date is reached there is nothing to block, so the prop expires by itself
  if (dateToValidate.getTime() <= today.getTime()) return null;

  /**
   * A date beyond the search window would leave the calendar with min > max: no selectable day,
   * and a search that could default to a date the brand does not even allow. Clamping is not an
   * option — moving the minimum down would unblock days that were asked to be blocked, and moving
   * the maximum up would widen the search window nobody asked to widen. So the configuration is
   * treated as unusable and ignored, which keeps the calendar behaving as if the prop was absent.
   */
  if (Number.isFinite(Number(maxDaysSearch))) {
    const maxDate = addDate(new Date(), maxDaysSearch);
    if (dateToValidate.getTime() > maxDate.getTime()) return null;
  }

  return dateToValidate;
}
